/**
 * Inventory Locations — fetches per-location inventory via App Proxy
 * and conditionally renders location-aware UI.
 *
 * Behaviour:
 *   >1 available location  → hide simple display, show multi-location table
 *   1 location             → replace simple display with "Ships from {name}, Available Quantity: {qty}"
 *   0 locations / error    → fall back to theme's default inventory display
 *
 * Also controls:
 *   - "Shipping Promise" label  → visible only when stocked at "Skynet International"
 *
 * DOM contract (per section):
 *   #Inventory-Wrapper-{sectionId}            [data-variant-id] [data-align]
 *     #Inventory-{sectionId}                  (simple / single-location display)
 *     #Inventory-Locations-{sectionId}        (multi-location table, .hidden)
 *       #Inventory-Locations-Body-{sectionId} (tbody)
 *   #Shipping-Promise-{sectionId}             (.hidden — placed via Custom Liquid block)
 */
(function () {
  'use strict';

  var PROXY_BASE = '/apps/multi-part-search/inventory-locations/';
  var SKYNET_LOCATION = 'Skynet International';

  // Inline style for each status/location line (SVG + text side-by-side)
  var LINE_STYLE = 'display:inline-flex;align-items:center;gap:0.4rem';

  /* ------------------------------------------------------------------ */
  /*  Bootstrap                                                          */
  /* ------------------------------------------------------------------ */
  var wrappers = document.querySelectorAll('[id^="Inventory-Wrapper-"]');
  if (!wrappers.length) return;

  wrappers.forEach(function (wrapper) {
    var sectionId = wrapper.id.replace('Inventory-Wrapper-', '');
    init(wrapper, sectionId);
  });

  /* ------------------------------------------------------------------ */
  /*  Per-section initialisation                                         */
  /* ------------------------------------------------------------------ */
  function init(wrapper, sectionId) {
    var variantId = wrapper.getAttribute('data-variant-id');
    if (!variantId) return;

    var simpleDisplay   = document.getElementById('Inventory-' + sectionId);
    var tableContainer  = document.getElementById('Inventory-Locations-' + sectionId);
    var tableBody       = document.getElementById('Inventory-Locations-Body-' + sectionId);
    var shippingPromise = document.getElementById('Shipping-Promise-' + sectionId);

    if (!simpleDisplay || !tableContainer || !tableBody) return;

    // Hide while API loads
    simpleDisplay.classList.add('inventory-loading');
    if (shippingPromise) shippingPromise.classList.add('hidden');

    // Initial fetch
    fetchAndRender(variantId, wrapper, simpleDisplay, tableContainer, tableBody, shippingPromise);

    // Listen for variant changes
    var productInfo = document.querySelector(
      'product-info[data-section="' + sectionId + '"]'
    );
    if (productInfo) {
      productInfo.addEventListener('change', function (e) {
        var input = e.target;
        if (!input || input.name !== 'id') return;

        var newVariantId = input.value;
        if (!newVariantId) return;

        wrapper.setAttribute('data-variant-id', newVariantId);

        // Reset UI while loading new variant
        simpleDisplay.classList.add('inventory-loading');
        resetLayout(simpleDisplay);
        tableContainer.classList.add('hidden');
        tableBody.innerHTML = '';
        if (shippingPromise) shippingPromise.classList.add('hidden');

        fetchAndRender(newVariantId, wrapper, simpleDisplay, tableContainer, tableBody, shippingPromise);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Fetch + Render                                                     */
  /* ------------------------------------------------------------------ */
  function fetchAndRender(variantId, wrapper, simpleDisplay, tableContainer, tableBody, shippingPromise) {
    fetch(PROXY_BASE + variantId, { method: 'POST' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        if (!data.success) {
          simpleDisplay.classList.remove('inventory-loading');
          return;
        }

        var locations = data.locations || [];

        // --- Shipping promise: show only when stocked at Skynet International ---
        var hasSkynet = locations.some(function (loc) {
          return loc.name.indexOf(SKYNET_LOCATION) !== -1;
        });
        if (shippingPromise) {
          if (hasSkynet) {
            shippingPromise.classList.remove('hidden');
          } else {
            shippingPromise.classList.add('hidden');
          }
        }

        // --- Inventory display ---
        if (locations.length > 1) {
          // Multiple locations — two-line header + table
          simpleDisplay.innerHTML =
            '<span style="' + LINE_STYLE + '">' +
              '<span class="svg-wrapper" style="color:rgb(62,214,96);display:inline-flex;flex-shrink:0">' +
                '<svg width="12" height="12"><circle cx="6" cy="6" r="4" fill="currentColor"/><circle cx="6" cy="6" r="5.5" fill="none" stroke="currentColor" stroke-width="1"/></svg>' +
              '</span>' +
              '<span>In stock</span>' +
            '</span>' +
            '<span style="' + LINE_STYLE + '">' +
              '<span class="svg-wrapper inventory-pin-icon" style="display:inline-flex;flex-shrink:0">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
              '</span>' +
              '<span>Available at multiple locations</span>' +
            '</span>';
          simpleDisplay.classList.remove('inventory-loading');
          applyLayout(simpleDisplay, wrapper);

          tableBody.innerHTML = '';

          locations.forEach(function (loc) {
            var row = document.createElement('tr');

            var nameCell = document.createElement('td');
            nameCell.textContent = loc.name;
            row.appendChild(nameCell);

            var qtyCell = document.createElement('td');
            qtyCell.textContent = loc.quantity;
            row.appendChild(qtyCell);

            tableBody.appendChild(row);
          });

          tableContainer.classList.remove('hidden');

        } else if (locations.length === 1 && locations[0].quantity > 0) {
          // Single location in stock — three-line display:
          //   Line 1: (green dot) In stock
          //   Line 2: Available Quantity: N  (larger font)
          //   Line 3: (pin) Ships from Name
          var loc = locations[0];

          simpleDisplay.innerHTML =
            '<span style="' + LINE_STYLE + '">' +
              '<span class="svg-wrapper" style="color:rgb(62,214,96);display:inline-flex;flex-shrink:0">' +
                '<svg width="12" height="12"><circle cx="6" cy="6" r="4" fill="currentColor"/><circle cx="6" cy="6" r="5.5" fill="none" stroke="currentColor" stroke-width="1"/></svg>' +
              '</span>' +
              '<span>In stock</span>' +
            '</span>' +
            '<span style="' + LINE_STYLE + ';font-size:1.15em"><span>Available Quantity: <strong>' + escapeHtml(String(loc.quantity)) + '</strong></span></span>' +
            '<span style="' + LINE_STYLE + '">' +
              '<span class="svg-wrapper inventory-pin-icon" style="display:inline-flex;flex-shrink:0">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
              '</span>' +
              '<span>Ships from <strong>' + escapeHtml(loc.name) + '</strong></span>' +
            '</span>';
          simpleDisplay.classList.remove('inventory-loading');
          applyLayout(simpleDisplay, wrapper);

        } else {
          // No locations returned — fall back to theme default
          simpleDisplay.classList.remove('inventory-loading');
        }
      })
      .catch(function (err) {
        simpleDisplay.classList.remove('inventory-loading');
        if (shippingPromise) shippingPromise.classList.add('hidden');
        console.warn('Inventory locations fetch failed:', err);
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Layout                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Apply all layout styles as inline styles on the <p>.
   * Inline styles beat ANY stylesheet rule, so Dawn's
   * .product__inventory { display: flex } cannot interfere.
   *
   * data-align on the wrapper is set by Liquid:
   *   "center"  when product.media.size == 0
   *   "left"    when product has images
   */
  function applyLayout(el, wrapper) {
    var align = wrapper.getAttribute('data-align') || 'left';
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.alignItems = (align === 'center') ? 'center' : 'flex-start';
    el.style.gap = '0.5rem';
    // Force full width so centering matches the page context,
    // not just the narrow content box. Dawn's wrapper has
    // align-items: flex-start which shrinks children otherwise.
    el.style.width = '100%';
  }

  /** Clear all inline layout styles (for variant-change reset). */
  function resetLayout(el) {
    el.style.display = '';
    el.style.flexDirection = '';
    el.style.alignItems = '';
    el.style.gap = '';
    el.style.width = '';
    el.style.textAlign = '';
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
})();
